import React, {ChangeEvent, useRef, useState} from "react";
import FCInput from "../../FCObjets/Form/FCInput";
import FCDatePicker from "../../FCObjets/Form/FCDatePicker";
import {CallbackErrorModalFL, CallbackFinishedModalFL} from "../../Dashboard/Form/ModalForm";
import {ExpenseDBInterface} from "../../../service/Business/Interfaces/ExpenseInterface";
import {AddExpense, UpdateExpense, UploadExpense} from "../../../service/Business/ExpenseService";
import {getIdWithToken} from "../../../utils/storage/Token";
import FCCheckbox from "../../FCObjets/Form/FCCheckbox";
import FCAlertMsg, {TypeAlertMsg} from "../../FCObjets/FCAlertMsg/FCAlertMsg";
import {useToast} from "../../FCObjets/FCToast/FCToast";
import "./FormAtelier.scss";

type FormBillingProps = {
    onFinished: CallbackFinishedModalFL;
    onError: CallbackErrorModalFL;
    dataExpense: ExpenseDBInterface | undefined;
};

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 Mo
const ACCEPTED_TYPES = "image/png, image/jpeg, .pdf";

const FormExpense: React.FunctionComponent<FormBillingProps> = ({dataExpense, onFinished, onError}) => {
    const toast = useToast();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [dateExpense, setDateExpense] = useState(dataExpense ? new Date(dataExpense.date) : null);
    const [inputDescription, setInputDescription] = useState(dataExpense ? dataExpense.description : "");
    const [inputPriceInclTax, setInputPriceInclTax] = useState(dataExpense ? dataExpense.priceInclTax.toString() : "0");
    const [inputVat, setInputVat] = useState(dataExpense ? (dataExpense.vat * 100).toString() : "0");
    const [inputCopyForYear, setInputCopyForYear] = useState(false);
    const [inputCompteFournisseur, setInputCompteFournisseur] = useState(dataExpense ? dataExpense.receiptLoaded === 2 : false);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

    const hasExistingReceipt = !!dataExpense && dataExpense.receiptLoaded === 1;
    const [file, setFile] = useState<File | undefined>(undefined);
    const [acknowledgeOverwrite, setAcknowledgeOverwrite] = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    /**
     * Sélection d'un nouveau justificatif (validation taille + reset confirmation)
     */
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (!selected) return;
        if (selected.size > MAX_FILE_SIZE) {
            toast.error("Le fichier dépasse 10 Mo.", "Justificatif");
            e.target.value = "";
            return;
        }
        setFile(selected);
        setAcknowledgeOverwrite(false);
    };

    const handleClearFile = () => {
        setFile(undefined);
        setAcknowledgeOverwrite(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    /**
     * Validation du formulaire
     */
    const handleSubmit = async () => {
        const errors: Record<string, string> = {};

        if (!inputDescription.trim()) errors.inputDescription = "La description est obligatoire.";
        if (!dateExpense) errors.dateExpense = "La date est obligatoire.";
        if (parseFloat(inputPriceInclTax) <= 0) errors.inputPriceInclTax = "Le prix TTC doit être supérieur à 0.";
        const vatValue = parseFloat(inputVat);
        if (isNaN(vatValue) || vatValue < 0 || vatValue > 100) errors.inputVat = "La TVA doit être entre 0 et 100.";

        // Garde-fou : remplacement d'un justificatif existant non confirmé
        if (file && hasExistingReceipt && !acknowledgeOverwrite) {
            toast.error("Veuillez confirmer le remplacement du justificatif existant.", "Justificatif");
            return;
        }

        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            toast.error(Object.values(errors).join(" "), "Validation");
            return;
        }
        setFieldErrors({});

        const expense: ExpenseDBInterface = {
            userCnx: undefined,
            date: dateExpense ? dateExpense.toDateString() : "",
            description: inputDescription,
            id: dataExpense ? dataExpense.id : -1,
            priceInclTax: parseFloat(inputPriceInclTax),
            vat: parseFloat(inputVat) / 100,
            copyForYear: inputCopyForYear,
            compteFournisseur: inputCompteFournisseur,
            receiptLoaded: dataExpense ? dataExpense.receiptLoaded : 0,
        };

        setIsSubmitting(true);
        try {
            let targetId = expense.id;
            if (dataExpense) {
                await UpdateExpense(expense.id, expense);
            } else {
                const res = await AddExpense(getIdWithToken(), expense);
                targetId = res.data?.id ?? -1;
            }

            if (file && targetId > 0) {
                await UploadExpense(getIdWithToken(), targetId, file);
            }

            toast.success(
                dataExpense
                    ? (file ? "Dépense et justificatif mis à jour." : "Dépense modifiée.")
                    : (file ? "Dépense ajoutée avec justificatif." : "Dépense ajoutée."),
                "Dépense",
            );
            onFinished();
        } catch (err) {
            onError(err);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <form className="fcaForm" onSubmit={(e) => e.preventDefault()}>
            {/* Informations principales */}
            <div className="fcaSection">
                <div className="fcaSectionLabel">Informations</div>

                <div className={`fcaFieldWrap${fieldErrors.inputDescription ? " fcaFieldWrap--error" : ""}`}>
                    <FCInput onChangeHandler={(e) => setInputDescription(e.target.value)} label={"Description"} id={"desc"}
                             value={inputDescription} widthInput={"100%"} type={"text"}/>
                    {fieldErrors.inputDescription && <span className="fcaFieldError">{fieldErrors.inputDescription}</span>}
                </div>

                <div className="fcaRow">
                    <div className={`fcaFieldWrap${fieldErrors.inputPriceInclTax ? " fcaFieldWrap--error" : ""}`}>
                        <FCInput value={inputPriceInclTax} type={"text"} widthInput={"100%"} id={"priceInclTax"}
                                 label={"Prix TTC"}
                                 min={0} max={1} step={"0.01"} pattern="[0-1]{1}([,\.][0-9]{0,2})?"
                                 onChangeHandler={(event) => {
                                     const value = event.target.value.replaceAll(',', ".");
                                     const numberVal = parseFloat(value);
                                     if (!isNaN(numberVal)) {
                                         setInputPriceInclTax(value);
                                     }
                                 }}/>
                        {fieldErrors.inputPriceInclTax && <span className="fcaFieldError">{fieldErrors.inputPriceInclTax}</span>}
                    </div>

                    <div className={`fcaFieldWrap${fieldErrors.inputVat ? " fcaFieldWrap--error" : ""}`}>
                        <FCInput value={inputVat} type={"text"} widthInput={"100%"} id={"vat"} label={"TVA (%)"}
                                 min={0} max={1} step={"0.01"} pattern="[0-1]{1}([,\.][0-9]{0,2})?"
                                 onChangeHandler={(event) => {
                                     const value = event.target.value.replaceAll(',', ".");
                                     const numberVal = parseFloat(value);
                                     if (!isNaN(numberVal) && numberVal >= 0 && numberVal <= 100) {
                                         setInputVat(value);
                                     }
                                 }}/>
                        {fieldErrors.inputVat && <span className="fcaFieldError">{fieldErrors.inputVat}</span>}
                    </div>

                    <div className={`fcaFieldWrap${fieldErrors.dateExpense ? " fcaFieldWrap--error" : ""}`}>
                        <FCDatePicker onChangeHandler={setDateExpense} value={dateExpense} id={"date"} label={"Date"}/>
                        {fieldErrors.dateExpense && <span className="fcaFieldError">{fieldErrors.dateExpense}</span>}
                    </div>
                </div>

                <FCCheckbox onChangeHandler={(e) => setInputCompteFournisseur(e.target.checked)}
                            label={"Justificatif compte fournisseur"} id={"compteFournisseur"}
                            value={inputCompteFournisseur} widthLabel={"260px"}/>

                {dataExpense === undefined &&
                    <FCCheckbox onChangeHandler={(e) => setInputCopyForYear(e.target.checked)}
                                label={"Copier la dépense pour l'année en cours"} id={"copyForYear"}
                                value={inputCopyForYear} widthLabel={"260px"}/>
                }
            </div>

            {/* Justificatif */}
            {!inputCompteFournisseur && (
                <div className="fcaSection">
                    <div className="fcaSectionLabel">Justificatif</div>

                    {hasExistingReceipt && !file && (
                        <div className="fcaInlineNotice">
                            <span className="material-symbols-outlined">task_alt</span>
                            <div className="fcaInlineNoticeContent">
                                <div className="fcaInlineNoticeTitle">Un justificatif est déjà archivé</div>
                                <div className="fcaInlineNoticeDesc">
                                    Sélectionnez un nouveau fichier ci-dessous pour le remplacer.
                                </div>
                            </div>
                        </div>
                    )}

                    {file ? (
                        <div className="fcaFileChip">
                            <span className="material-symbols-outlined fcaFileChipIcon">description</span>
                            <div className="fcaFileChipBody">
                                <div className="fcaFileChipName">{file.name}</div>
                                <div className="fcaFileChipMeta">{(file.size / 1024).toFixed(0)} Ko</div>
                            </div>
                            <button type="button" className="fcaFileChipRemove"
                                    onClick={handleClearFile} title="Retirer le fichier">
                                <span className="material-symbols-outlined">close</span>
                            </button>
                        </div>
                    ) : (
                        <div className="fcaDropZone" onClick={() => fileInputRef.current?.click()}>
                            <span className="material-symbols-outlined">upload_file</span>
                            <div className="fcaDropZoneTitle">
                                {hasExistingReceipt ? "Remplacer le justificatif" : "Ajouter un justificatif"}
                            </div>
                            <div className="fcaDropZoneHint">PDF, JPG, PNG (max 10 Mo)</div>
                        </div>
                    )}
                    <input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES}
                           style={{display: "none"}} onChange={handleFileChange}/>

                    {file && hasExistingReceipt && (
                        <FCAlertMsg
                            activate={true}
                            type={TypeAlertMsg.WARNING}
                            title="Remplacement du justificatif"
                            textMsg="Un justificatif est déjà archivé. Cette action écrasera le fichier existant."
                        />
                    )}

                    {file && hasExistingReceipt && (
                        <FCCheckbox onChangeHandler={(e) => setAcknowledgeOverwrite(e.target.checked)}
                                    label={"Je confirme remplacer le justificatif existant"}
                                    id={"acknowledgeOverwrite"}
                                    value={acknowledgeOverwrite} widthLabel={"320px"}/>
                    )}
                </div>
            )}

            <div className="fcaActions">
                <button type="button" className="fcaBtn fcaBtnSecondary"
                        onClick={() => onFinished()} disabled={isSubmitting}>
                    Annuler
                </button>
                <button type="button" className="fcaBtn fcaBtnPrimary"
                        onClick={handleSubmit} disabled={isSubmitting}>
                    <span className="material-symbols-outlined">check</span>
                    {isSubmitting ? "Enregistrement…" : "Valider"}
                </button>
            </div>
        </form>
    );
}

export default FormExpense;
